import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AnimalDocumentCategory } from '../../models/animal-document-category.enum';
import { CareCondition, NutritionCondition } from '../../models/body-condition-score.model';
import { AnimalMedicalConditionModel, AnimalMedicalConditionUpdatePayload } from '../../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordCreatePayload } from '../../models/medical/animal-medical-record.model';
import {
  AnimalPreventiveTreatmentModel,
  AnimalPreventiveTreatmentUpdatePayload
} from '../../models/medical/animal-preventive-treatment.model';
import { AnimalQuarantineModel, AnimalQuarantineUpdatePayload } from '../../models/medical/animal-quarantine.model';
import {
  MedicalConditionKind,
  MedicalConditionSeverity,
  MedicalRecordType,
  PreventiveTreatmentCategory,
  PreventiveTreatmentType,
  QuarantineReason
} from '../../models/medical/medical.enums';
import { isoDatePart, kgToGrams, parseIsoDate, toIsoDateString } from '../../models/medical/medical.helpers';
import { MedicalSaveOperation, MedicalUpload, MedicalUploadTarget } from '../../services/medical-record-save.service';

let keySequence = 0;
export function nextKey(prefix: string): string {
  return `${prefix}-new-${++keySequence}`;
}

// --- Formular-Typen ---------------------------------------------------------------------------

export type TreatmentForm = FormGroup<{
  key: FormControl<string>;
  id: FormControl<number | null>;
  category: FormControl<PreventiveTreatmentCategory>;
  types: FormControl<PreventiveTreatmentType[]>;
  preparation: FormControl<string | null>;
  batchNumber: FormControl<string | null>;
  dose: FormControl<string | null>;
  frequency: FormControl<string | null>;
  nextDueAt: FormControl<Date | null>;
  /** Von Hand geändert: kein automatischer Vorschlag mehr. */
  nextDueManual: FormControl<boolean>;
  noFurtherDue: FormControl<boolean>;
  validUntil: FormControl<Date | null>;
  notes: FormControl<string | null>;
}>;

export type ConditionForm = FormGroup<{
  key: FormControl<string>;
  id: FormControl<number | null>;
  kind: FormControl<MedicalConditionKind | null>;
  label: FormControl<string>;
  severity: FormControl<MedicalConditionSeverity | null>;
  visibleToAdopter: FormControl<boolean>;
  diagnosedAt: FormControl<Date | null>;
  diagnosedAtManual: FormControl<boolean>;
  resolvedAt: FormControl<Date | null>;
  notes: FormControl<string | null>;
}>;

export type QuarantineForm = FormGroup<{
  reason: FormControl<QuarantineReason | null>;
  startsAt: FormControl<Date | null>;
  startsAtManual: FormControl<boolean>;
  endsAt: FormControl<Date | null>;
  unlimited: FormControl<boolean>;
  authorityId: FormControl<number | null>;
  blocksAdoption: FormControl<boolean>;
  notes: FormControl<string | null>;
}>;

/** Fortschreiben einer aktiven Diagnose. */
export type ContinueConditionForm = FormGroup<{
  resolvedAt: FormControl<Date | null>;
  severity: FormControl<MedicalConditionSeverity | null>;
  visibleToAdopter: FormControl<boolean>;
}>;

/** Fortschreiben einer aktiven oder geplanten Quarantäne. */
export type ContinueQuarantineForm = FormGroup<{
  endsAt: FormControl<Date | null>;
  unlimited: FormControl<boolean>;
  blocksAdoption: FormControl<boolean>;
}>;

/** Fortschreiben einer Behandlung mit offener Fälligkeit. */
export type ContinueTreatmentForm = FormGroup<{
  nextDueAt: FormControl<Date | null>;
  noFurtherDue: FormControl<boolean>;
}>;

export type RecordForm = FormGroup<{
  date: FormControl<Date | null>;
  type: FormControl<MedicalRecordType | null>;
  veterinarianId: FormControl<number | null>;
  weightKg: FormControl<number | null>;
  bcsNutrition: FormControl<NutritionCondition | null>;
  careCondition: FormControl<CareCondition[] | null>;
  finding: FormControl<string | null>;
  treatment: FormControl<string | null>;
  treatments: FormArray<TreatmentForm>;
  conditions: FormArray<ConditionForm>;
  quarantineEnabled: FormControl<boolean>;
  quarantine: QuarantineForm;
}>;

export interface ContinueItem<TOriginal, TForm> {
  key: string;
  original: TOriginal;
  form: TForm;
}

export interface AttachmentRow {
  key: string;
  file: File;
  target: MedicalUploadTarget;
  category: AnimalDocumentCategory;
  date: Date | null;
  dateManual: boolean;
  previewUrl: string | null;
  /** Bleibt über Nachtragen-Versuche erhalten (Video bereits hochgeladen). */
  upload: MedicalUpload | null;
}

// --- Validatoren ------------------------------------------------------------------------------

/** Ende ≥ Beginn, sofern nicht unbefristet. */
export const quarantinePeriodValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const startsAt = group.get('startsAt')?.value as Date | null;
  const endsAt = group.get('endsAt')?.value as Date | null;
  const unlimited = group.get('unlimited')?.value as boolean;
  if (unlimited || !startsAt || !endsAt) return null;
  return toIsoDateString(endsAt)! < toIsoDateString(startsAt)! ? { endBeforeStart: true } : null;
};

const notBlank: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  typeof control.value === 'string' && control.value.trim() === '' ? { required: true } : null;

// --- Fabriken ---------------------------------------------------------------------------------

export function createRecordForm(): RecordForm {
  return new FormGroup({
    date: new FormControl<Date | null>(new Date(), Validators.required),
    type: new FormControl<MedicalRecordType | null>(null),
    veterinarianId: new FormControl<number | null>(null),
    weightKg: new FormControl<number | null>(null, Validators.min(0)),
    bcsNutrition: new FormControl<NutritionCondition | null>(null),
    careCondition: new FormControl<CareCondition[] | null>([]),
    finding: new FormControl<string | null>(null),
    treatment: new FormControl<string | null>(null),
    treatments: new FormArray<TreatmentForm>([]),
    conditions: new FormArray<ConditionForm>([]),
    quarantineEnabled: new FormControl(false, { nonNullable: true }),
    quarantine: createQuarantineForm(),
  });
}

export function createTreatmentForm(
  category: PreventiveTreatmentCategory,
  source?: AnimalPreventiveTreatmentModel
): TreatmentForm {
  return new FormGroup({
    key: new FormControl(source ? `t-${source.id}` : nextKey('t'), { nonNullable: true }),
    id: new FormControl<number | null>(source?.id ?? null),
    category: new FormControl(source?.category ?? category, { nonNullable: true, validators: Validators.required }),
    types: new FormControl<PreventiveTreatmentType[]>(source?.types ?? [], { nonNullable: true }),
    preparation: new FormControl<string | null>(source?.preparation ?? null),
    batchNumber: new FormControl<string | null>(source?.batchNumber ?? null),
    dose: new FormControl<string | null>(source?.dose ?? null),
    frequency: new FormControl<string | null>(source?.frequency ?? null),
    nextDueAt: new FormControl<Date | null>({ value: parseIsoDate(source?.nextDueAt), disabled: !!source && !source.nextDueAt }),
    nextDueManual: new FormControl(!!source, { nonNullable: true }),
    noFurtherDue: new FormControl(!!source && !source.nextDueAt, { nonNullable: true }),
    validUntil: new FormControl<Date | null>(parseIsoDate(source?.validUntil)),
    notes: new FormControl<string | null>(source?.notes ?? null),
  });
}

export function createConditionForm(recordDate: Date | null, source?: AnimalMedicalConditionModel): ConditionForm {
  return new FormGroup({
    key: new FormControl(source ? `c-${source.id}` : nextKey('c'), { nonNullable: true }),
    id: new FormControl<number | null>(source?.id ?? null),
    kind: new FormControl<MedicalConditionKind | null>(source?.kind ?? MedicalConditionKind.DIAGNOSIS, Validators.required),
    label: new FormControl(source?.label ?? '', { nonNullable: true, validators: [Validators.required, notBlank] }),
    severity: new FormControl<MedicalConditionSeverity | null>(source?.severity ?? null),
    visibleToAdopter: new FormControl(source?.visibleToAdopter ?? false, { nonNullable: true }),
    diagnosedAt: new FormControl<Date | null>(source ? parseIsoDate(source.diagnosedAt) : startOfDay(recordDate)),
    diagnosedAtManual: new FormControl(!!source, { nonNullable: true }),
    resolvedAt: new FormControl<Date | null>(parseIsoDate(source?.resolvedAt)),
    notes: new FormControl<string | null>(source?.notes ?? null),
  });
}

export function createQuarantineForm(): QuarantineForm {
  return new FormGroup({
    reason: new FormControl<QuarantineReason | null>(null, Validators.required),
    startsAt: new FormControl<Date | null>(null, Validators.required),
    startsAtManual: new FormControl(false, { nonNullable: true }),
    endsAt: new FormControl<Date | null>(null),
    unlimited: new FormControl(false, { nonNullable: true }),
    authorityId: new FormControl<number | null>(null),
    blocksAdoption: new FormControl(true, { nonNullable: true }),
    notes: new FormControl<string | null>(null),
  }, { validators: quarantinePeriodValidator });
}

export function createContinueConditionForm(condition: AnimalMedicalConditionModel): ContinueConditionForm {
  return new FormGroup({
    resolvedAt: new FormControl<Date | null>(parseIsoDate(condition.resolvedAt)),
    severity: new FormControl<MedicalConditionSeverity | null>(condition.severity ?? null),
    visibleToAdopter: new FormControl(condition.visibleToAdopter, { nonNullable: true }),
  });
}

export function createContinueQuarantineForm(quarantine: AnimalQuarantineModel): ContinueQuarantineForm {
  return new FormGroup({
    endsAt: new FormControl<Date | null>({ value: parseIsoDate(quarantine.endsAt), disabled: !quarantine.endsAt }),
    unlimited: new FormControl(!quarantine.endsAt, { nonNullable: true }),
    blocksAdoption: new FormControl(quarantine.blocksAdoption, { nonNullable: true }),
  });
}

export function createContinueTreatmentForm(treatment: AnimalPreventiveTreatmentModel): ContinueTreatmentForm {
  return new FormGroup({
    nextDueAt: new FormControl<Date | null>(parseIsoDate(treatment.nextDueAt)),
    noFurtherDue: new FormControl(false, { nonNullable: true }),
  });
}

export function startOfDay(date: Date | null): Date | null {
  return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
}

// --- Payloads ---------------------------------------------------------------------------------

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function buildRecordPayload(
  form: RecordForm,
  animalId: number,
  correctsEntryId: number | null
): AnimalMedicalRecordCreatePayload {
  const value = form.getRawValue();
  const veterinarianId = value.veterinarianId;
  return {
    animal: { id: animalId },
    date: value.date!.toISOString(),
    type: value.type ?? null,
    veterinarian: veterinarianId ? { id: veterinarianId } : null,
    weight: kgToGrams(value.weightKg),
    finding: trimOrNull(value.finding),
    treatment: trimOrNull(value.treatment),
    careCondition: value.careCondition?.length ? value.careCondition : null,
    bcsNutrition: value.bcsNutrition ?? null,
    correctsEntry: correctsEntryId ? { id: correctsEntryId } : null,
  };
}

function treatmentFields(group: TreatmentForm, recordDate: Date, veterinarianId: number | null) {
  const value = group.getRawValue();
  const isMedication = value.category === PreventiveTreatmentCategory.MEDICATION;
  return {
    category: value.category,
    types: value.types ?? [],
    preparation: trimOrNull(value.preparation),
    batchNumber: trimOrNull(value.batchNumber),
    dose: isMedication ? trimOrNull(value.dose) : null,
    frequency: isMedication ? trimOrNull(value.frequency) : null,
    administeredAt: toIsoDateString(recordDate)!,
    nextDueAt: value.noFurtherDue ? null : toIsoDateString(value.nextDueAt),
    validUntil: toIsoDateString(value.validUntil),
    veterinarian: veterinarianId ? { id: veterinarianId } : null,
    notes: trimOrNull(value.notes),
  };
}

function conditionFields(group: ConditionForm) {
  const value = group.getRawValue();
  return {
    kind: value.kind!,
    label: value.label.trim(),
    severity: value.severity ?? null,
    diagnosedAt: toIsoDateString(value.diagnosedAt),
    resolvedAt: toIsoDateString(value.resolvedAt),
    visibleToAdopter: value.visibleToAdopter,
    notes: trimOrNull(value.notes),
  };
}

export interface OperationContext {
  animalId: number;
  recordDate: Date;
  veterinarianId: number | null;
  /** Korrektur: Ids der Positionen, die an der alten Karteikarte hingen. */
  carriedTreatmentIds: number[];
  carriedConditionIds: number[];
  continueConditions: ContinueItem<AnimalMedicalConditionModel, ContinueConditionForm>[];
  continueQuarantines: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>[];
  continueTreatments: ContinueItem<AnimalPreventiveTreatmentModel, ContinueTreatmentForm>[];
}

/** Baut alle Positionen, die zusammen mit der Karteikarte gespeichert werden. */
export function buildOperations(form: RecordForm, ctx: OperationContext): MedicalSaveOperation[] {
  const operations: MedicalSaveOperation[] = [];
  const animal = { id: ctx.animalId };

  const keptTreatmentIds = new Set<number>();
  for (const group of form.controls.treatments.controls) {
    const key = group.controls.key.value;
    const id = group.controls.id.value;
    const fields = treatmentFields(group, ctx.recordDate, ctx.veterinarianId);
    if (id !== null) {
      keptTreatmentIds.add(id);
      operations.push({ kind: 'updateTreatment', key, id, payload: fields, relink: true });
    } else {
      operations.push({ kind: 'createTreatment', key, payload: { animal, ...fields } });
    }
  }
  for (const id of ctx.carriedTreatmentIds) {
    if (!keptTreatmentIds.has(id)) operations.push({ kind: 'deleteTreatment', key: `del-t-${id}`, id });
  }

  const keptConditionIds = new Set<number>();
  for (const group of form.controls.conditions.controls) {
    const key = group.controls.key.value;
    const id = group.controls.id.value;
    const fields = conditionFields(group);
    if (id !== null) {
      keptConditionIds.add(id);
      operations.push({ kind: 'updateCondition', key, id, payload: fields, relink: true });
    } else {
      operations.push({ kind: 'createCondition', key, payload: { animal, ...fields } });
    }
  }
  for (const id of ctx.carriedConditionIds) {
    if (!keptConditionIds.has(id)) operations.push({ kind: 'deleteCondition', key: `del-c-${id}`, id });
  }

  if (form.controls.quarantineEnabled.value) {
    const value = form.controls.quarantine.getRawValue();
    operations.push({
      kind: 'createQuarantine',
      key: 'q',
      payload: {
        animal,
        reason: value.reason!,
        startsAt: toIsoDateString(value.startsAt)!,
        endsAt: value.unlimited ? null : toIsoDateString(value.endsAt),
        blocksAdoption: value.blocksAdoption,
        authority: value.authorityId ? { id: value.authorityId } : null,
        notes: trimOrNull(value.notes),
      }
    });
  }

  for (const item of ctx.continueConditions) {
    const payload = continueConditionChanges(item);
    if (payload) operations.push({ kind: 'updateCondition', key: item.key, id: item.original.id, payload, relink: false });
  }
  for (const item of ctx.continueQuarantines) {
    const payload = continueQuarantineChanges(item);
    if (payload) operations.push({ kind: 'updateQuarantine', key: item.key, id: item.original.id, payload });
  }
  for (const item of ctx.continueTreatments) {
    const payload = continueTreatmentChanges(item);
    if (payload) operations.push({ kind: 'updateTreatment', key: item.key, id: item.original.id, payload, relink: false });
  }

  return operations;
}

/** Nur geänderte Felder (PUT ist Merge); `null`, wenn nichts geändert ist. */
export function continueConditionChanges(
  item: ContinueItem<AnimalMedicalConditionModel, ContinueConditionForm>
): AnimalMedicalConditionUpdatePayload | null {
  const value = item.form.getRawValue();
  const changes: AnimalMedicalConditionUpdatePayload = {};
  const resolvedAt = toIsoDateString(value.resolvedAt);
  if (resolvedAt !== (isoDatePart(item.original.resolvedAt) ?? null)) changes.resolvedAt = resolvedAt;
  if ((value.severity ?? null) !== (item.original.severity ?? null)) changes.severity = value.severity ?? null;
  if (value.visibleToAdopter !== item.original.visibleToAdopter) changes.visibleToAdopter = value.visibleToAdopter;
  return Object.keys(changes).length ? changes : null;
}

export function continueQuarantineChanges(
  item: ContinueItem<AnimalQuarantineModel, ContinueQuarantineForm>
): AnimalQuarantineUpdatePayload | null {
  const value = item.form.getRawValue();
  const changes: AnimalQuarantineUpdatePayload = {};
  const endsAt = value.unlimited ? null : toIsoDateString(value.endsAt);
  if (endsAt !== (isoDatePart(item.original.endsAt) ?? null)) changes.endsAt = endsAt;
  if (value.blocksAdoption !== item.original.blocksAdoption) changes.blocksAdoption = value.blocksAdoption;
  return Object.keys(changes).length ? changes : null;
}

export function continueTreatmentChanges(
  item: ContinueItem<AnimalPreventiveTreatmentModel, ContinueTreatmentForm>
): AnimalPreventiveTreatmentUpdatePayload | null {
  const value = item.form.getRawValue();
  const nextDueAt = value.noFurtherDue ? null : toIsoDateString(value.nextDueAt);
  return nextDueAt !== (isoDatePart(item.original.nextDueAt) ?? null) ? { nextDueAt } : null;
}

export function carriedIds<T extends { id: number }>(items: T[] | undefined): number[] {
  return (items ?? []).map(item => item.id);
}
